import type { ReactElement, SVGProps } from 'react';

/**
 * Inline single-stroke icons.
 *
 * They are decorative by default — every control that uses one carries its own
 * accessible name — so each renders `aria-hidden`. Size comes from the caller's
 * class names; the 24px attributes are only a fallback for unstyled use.
 */
export type IconComponent = (props: SVGProps<SVGSVGElement>) => ReactElement;

function Icon(props: SVGProps<SVGSVGElement>): ReactElement {
  const { children, ...rest } = props;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={24}
      height={24}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export function Plus(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="M12 5v14M5 12h14" />
    </Icon>
  );
}

export function FolderOpen(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="M4 19V6a1 1 0 0 1 1-1h4l2 2h7a1 1 0 0 1 1 1v2" />
      <path d="M4 20a1 1 0 0 1-.96-1.28l2.1-7.2A1 1 0 0 1 6.1 10.8H21l-2.1 8.5a1 1 0 0 1-.97.7z" />
    </Icon>
  );
}

export function Save(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <path d="M17 21v-8H7v8" />
      <path d="M7 3v5h7" />
    </Icon>
  );
}

export function Download(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="m7 10 5 5 5-5" />
      <path d="M12 15V3" />
    </Icon>
  );
}

export function Upload(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="m7 8 5-5 5 5" />
      <path d="M12 3v12" />
    </Icon>
  );
}

export function Undo(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="M9 14 4 9l5-5" />
      <path d="M4 9h11a5 5 0 0 1 0 10h-4" />
    </Icon>
  );
}

export function Redo(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="m15 14 5-5-5-5" />
      <path d="M20 9H9a5 5 0 0 0 0 10h4" />
    </Icon>
  );
}

export function Wand(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="m3 21 8.5-8.5" />
      <path d="m12 12 4-4-4-4-4 4z" />
      <path d="M18 13v3M16.5 14.5h3M19 3v3M17.5 4.5h3" />
    </Icon>
  );
}

export function Layout(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 21V9" />
    </Icon>
  );
}

export function Sun(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </Icon>
  );
}

export function Moon(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </Icon>
  );
}

export function Monitor(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <rect x="3" y="4" width="18" height="12" rx="1.5" />
      <path d="M8 20h8M12 16v4" />
    </Icon>
  );
}

export function Settings(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.04 1.56V21a2 2 0 1 1-4 0v-.06A1.7 1.7 0 0 0 8.9 19.4a1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15.1 1.7 1.7 0 0 0 3.06 14H3a2 2 0 1 1 0-4h.06A1.7 1.7 0 0 0 4.6 8.9a1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 8.9 4.6h.1A1.7 1.7 0 0 0 10 3.06V3a2 2 0 1 1 4 0v.06a1.7 1.7 0 0 0 1.04 1.54 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87v.1a1.7 1.7 0 0 0 1.54 1.04H21a2 2 0 1 1 0 4h-.06A1.7 1.7 0 0 0 19.4 15z" />
    </Icon>
  );
}

export function HelpCircle(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.2 9.2A3 3 0 0 1 15 10c0 2-3 2.6-3 4.2" />
      <path d="M12 17.5h.01" />
    </Icon>
  );
}

export function Search(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.7-3.7" />
    </Icon>
  );
}

export function ZoomIn(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.7-3.7M8 11h6M11 8v6" />
    </Icon>
  );
}

export function ZoomOut(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.7-3.7M8 11h6" />
    </Icon>
  );
}

export function Maximize(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
    </Icon>
  );
}

export function Grid(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
    </Icon>
  );
}

function MapIcon(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="m3 6 6-3 6 3 6-3v15l-6 3-6-3-6 3z" />
      <path d="M9 3v15M15 6v15" />
    </Icon>
  );
}

export function Code(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="m8 6-6 6 6 6M16 6l6 6-6 6" />
    </Icon>
  );
}

export function Workflow(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <rect x="3" y="3" width="8" height="6" rx="1.5" />
      <rect x="13" y="15" width="8" height="6" rx="1.5" />
      <path d="M7 9v5a2 2 0 0 0 2 2h4" />
    </Icon>
  );
}

export function Columns(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M12 4v16" />
    </Icon>
  );
}

export function AlertTriangle(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="M10.3 3.9 2.3 17.8A2 2 0 0 0 4 21h16a2 2 0 0 0 1.7-3.2l-8-13.9a2 2 0 0 0-3.4 0z" />
      <path d="M12 9v4.5M12 17.2h.01" />
    </Icon>
  );
}

export function AlertCircle(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5.5M12 16.2h.01" />
    </Icon>
  );
}

export function Info(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5.5M12 7.8h.01" />
    </Icon>
  );
}

export function Check(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="m4 12.5 5 5L20 6.5" />
    </Icon>
  );
}

function CloseIcon(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="m6 6 12 12M18 6 6 18" />
    </Icon>
  );
}

export function ChevronDown(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="m6 9 6 6 6-6" />
    </Icon>
  );
}

export function ChevronRight(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="m9 6 6 6-6 6" />
    </Icon>
  );
}

export function ChevronLeft(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="m15 6-6 6 6 6" />
    </Icon>
  );
}

export function MoreHorizontal(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon strokeWidth={2.5} {...props}>
      <path d="M5 12h.01M12 12h.01M19 12h.01" />
    </Icon>
  );
}

export function Copy(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
    </Icon>
  );
}

export function Trash(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="M4 7h16" />
      <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
      <path d="m6 7 .9 12.1a1 1 0 0 0 1 .9h8.2a1 1 0 0 0 1-.9L18 7" />
      <path d="M10 11v6M14 11v6" />
    </Icon>
  );
}

export function Edit3(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="M12 20h9" />
      <path d="M16.4 3.6a2.3 2.3 0 0 1 3.2 3.2L7.6 18.8 3.5 20l1.2-4.1z" />
    </Icon>
  );
}

function LinkIcon(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="M10.5 13.5a4.5 4.5 0 0 0 6.4 0l2.6-2.6a4.5 4.5 0 0 0-6.4-6.4l-1.3 1.3" />
      <path d="M13.5 10.5a4.5 4.5 0 0 0-6.4 0l-2.6 2.6a4.5 4.5 0 0 0 6.4 6.4l1.3-1.3" />
    </Icon>
  );
}

export function Share(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
      <path d="m8 6 4-4 4 4" />
      <path d="M12 2v13" />
    </Icon>
  );
}

function ListIcon(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01" />
    </Icon>
  );
}

export function Play(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="m8 5 11 7-11 7z" />
    </Icon>
  );
}

export function Pause(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="M9 5v14M15 5v14" />
    </Icon>
  );
}

function FunctionIcon(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="M7 21c0-8 .2-13 1.9-15.2A3 3 0 0 1 13.5 5.6" />
      <path d="M5.5 11h7" />
      <path d="m14.5 14 5.5 6M20 14l-5.5 6" />
    </Icon>
  );
}

export function Layers(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="m12 2 9 5-9 5-9-5z" />
      <path d="m3 12 9 5 9-5" />
      <path d="m3 17 9 5 9-5" />
    </Icon>
  );
}

function TypeIcon(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="M4 7V5h16v2" />
      <path d="M12 5v14M9 19h6" />
    </Icon>
  );
}

export function Sparkles(props: SVGProps<SVGSVGElement>): ReactElement {
  return (
    <Icon {...props}>
      <path d="m12 3 1.7 4.8L18.5 9.5l-4.8 1.7L12 16l-1.7-4.8L5.5 9.5l4.8-1.7z" />
      <path d="M18.5 15v3M17 16.5h3M5.5 4v2.5M4.2 5.2h2.6" />
    </Icon>
  );
}

/*
 * `Function`, `Map`, `List`, `Type` and `X` shadow globals when imported, so the
 * implementations are declared under safe local names and only the public
 * export carries the short name.
 */
export {
  FunctionIcon as Function,
  MapIcon as Map,
  ListIcon as List,
  TypeIcon as Type,
  LinkIcon as Link,
  CloseIcon as X,
};
